<template>
  <BrandLinksHead
    v-if="brandSettings"
    :title="reportTitle"
    :brand-settings="brandSettings"
    :fav-icon-source="accountSettings.website"
  />
  <div
    :class="[
      'body-text-branding titles-branding relative flex flex-col w-full max-w-[1920px] mx-auto bg-default-50',
      'xl:flex-row',
      (!hideCover && reportDigitalDesignContentData.digitalCover.hasCover) && 'h-dvh'
    ]"
    :style="{ ...bodyTextStyling, ...titlesStyling }"
  >
    <div
      :class="[
        'xl:border-r border-default-300 z-50 header',
        'xl:w-72 xl:flex-none xl:block xl:bg-default-50',
        'sticky top-0 flex justify-between max-xl:gap-sm max-xl:p-large bg-white'
      ]"
      :style="headerStyles"
    >
      <div class="min-w-0 xl:flex hidden sticky top-0 flex-col justify-between h-screen">
        <div class="flex-auto overflow-y-auto p-large">
          <img
            v-if="reportDigitalDesignContentData.reportColors.hasCustomLogo"
            :src="reportDigitalDesignContentData.reportColors.logo"
            :alt="`${company.name}'s logo`"
            class="h-20 mb-xl"
          >
          <img
            v-else-if="brandSettings.logos?.square?.url && reportDigitalDesignContentData.reportColors.logo === 'square'"
            :src="brandSettings.logos.square.url"
            :alt="`${company.name}'s logo`"
            class="w-10 h-10 mb-xl aspect-square object-center object-cover"
          >
          <img
            v-else-if="brandSettings.logos?.rectangle?.url && reportDigitalDesignContentData.reportColors.logo === 'rectangle'"
            :src="brandSettings.logos.rectangle.url"
            :alt="`${company.name}'s logo`"
            class="h-20 mb-xl object-contain"
          >
          <h1
            class="titles-branding text-2xl mb-base"
            :style="{
              ...titlesStyling,
              color: reportDigitalDesignContentData.reportColors.titlesAndBodyTextTitleColour,
            }"
          >
            {{ reportStore.reportTitle }}
          </h1>
          <div
            v-if="address"
            class="body-text-branding flex flex-col text-sm mb-xl"
            :style="{
              color: reportDigitalDesignContentData.reportColors.titlesAndBodyTextBodyColour,
            }"
          >
            <span class="truncate">
              {{ address.houseNumber }}
              {{ address.street }}
            </span>
            <span class="truncate">
              {{ address.locality ? `${address.locality.toLowerCase().replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase())}, ` : '' }}
              {{ address.town ? `${address.town.toLowerCase().replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase())}, ` : '' }}
              {{ address.postcode }}
            </span>
          </div>
          <hr class="mb-xl">
          <ExpandedReportNavigation
            :brand-settings="brandSettings"
            :report-digital-design-content-data="reportDigitalDesignContentData"
          />
        </div>
        <div
          v-if="!reportStore.settings.hideContactDetails"
          class="p-large"
          :style="{ color: reportDigitalDesignContentData.reportColors.titlesAndBodyTextBodyColour }"
        >
          <p
            v-if="userSettings?.name"
            class="font-medium"
          >
            {{ userSettings.name }}
          </p>
          <p
            v-if="userSettings?.jobRole"
            class="font-medium"
          >
            {{ userSettings.jobRole }}
          </p>
          <hr class="my-xs">
          <p v-if="reportData.account?.name">
            {{ reportData.account?.name }}
          </p>
          <p v-if="accountSettings.address">
            {{ accountSettings.address }}
          </p>
        </div>
      </div>
      <!-- Mobile sidebar header -->
      <div
        :class="[
          'flex justify-between space-x-base items-center w-full',
          'xl:hidden',
        ]"
      >
        <div>
          <img
            v-if="reportDigitalDesignContentData.mobileHeader.hasCustomLogo"
            :src="reportDigitalDesignContentData.mobileHeader.logo"
            :alt="`${company.name}'s logo`"
            class="h-12 w-48 object-contain"
          >
          <img
            v-else-if="brandSettings.logos.square?.url && reportDigitalDesignContentData.mobileHeader.logo === 'square'"
            :src="brandSettings.logos.square.url"
            :alt="`${company.name}'s logo`"
            class="size-12 aspect-square object-center object-contain"
          >
          <img
            v-else-if="brandSettings.logos?.rectangle?.url && reportDigitalDesignContentData.mobileHeader.logo === 'rectangle'"
            :src="brandSettings.logos.rectangle.url"
            :alt="`${company.name}'s logo`"
            class="h-12 w-48 object-contain"
          >
          <h3
            v-else
            class="text-default-700 font-bold titles-branding titles text-2xl text-wrap"
          >
            {{ reportData?.account?.name ?? company.name }}
          </h3>
        </div>
        <SpectreButton
          :style="mobileButtonStyles"
          class="insights-button flex-shrink-0"
          @click="toggleShowModal(true)"
        >
          Contact Us
        </SpectreButton>
      </div>
    </div>
    <div
      v-if="!hideCover && reportDigitalDesignContentData.digitalCover.hasCover"
      class="flex-1 relative flex items-center"
      :style="{
        backgroundColor: reportDigitalDesignContentData.digitalCover.backgroundColour,
      }"
      @mouseenter="reportStore.setSelectedTab(null)"
    >
      <img
        v-if="reportDigitalDesignContentData.digitalCover.backgroundImageUrl !== 'none'"
        :src="reportDigitalDesignContentData.digitalCover.backgroundImageUrl"
        class="absolute inset-0 h-full w-full object-cover object-center"
        alt="Cover image"
        :style="{
          opacity: `${reportDigitalDesignContentData.digitalCover.backgroundImageTransparency}%`,
        }"
      >
      <div
        :class="[
          'px-base py-6 h-full',
          'lg:mx-24 lg:w-[46rem] lg:h-auto',
        ]"
      >
        <div
          :class="[
            'flex flex-col h-full rounded-lg w-full relative px-6 py-10 space-y-base',
            'sm:px-10',
            reportDigitalDesignContentData.digitalCover.textBackground === 'blurry' && 'backdrop-blur-lg',
          ]"
          :style="{
            backgroundColor: reportDigitalDesignContentData.digitalCover.textBackground === 'no_background' ? 'transparent' : rgba(reportDigitalDesignContentData.digitalCover.textBackgroundColour, reportDigitalDesignContentData.digitalCover.textBackground === 'blurry' ? .6 : 1),
          }"
        >
          <div class="flex-1 space-y-base">
            <h2
              class="titles-branding text-4xl font-bold"
              :style="{
                ...titlesStyling,
                color: reportDigitalDesignContentData.digitalCover.titleTextColour
              }"
            >
              {{ reportStore.reportTitle }}
            </h2>
            <p
              class="body-text-branding text-sm font-medium"
              :style="{
                ...bodyTextStyling,
                color: reportDigitalDesignContentData.digitalCover.bodyTextColour,
              }"
            >
              {{ reportStore.settings.anonymiseAddress ? reportStore.address.anonymisedFullAddress : reportStore.address.fullAddress }}
            </p>
            <hr class="border-default-300">
            <p
              v-if="reportStore.settings.recipients?.length"
              class="body-text-branding text-xs font-medium"
              :style="{
                ...bodyTextStyling,
                color: reportDigitalDesignContentData.digitalCover.bodyTextColour,
              }"
            >
              {{ reportStore.settings.recipientIntroduction }}
            </p>
            <ul v-if="reportStore.settings.recipients?.length">
              <li
                v-for="recipient in reportStore.settings.recipients"
                :key="recipient"
                class="body-text-branding font-bold"
                :style="{
                  ...bodyTextStyling,
                  color: reportDigitalDesignContentData.digitalCover.bodyTextColour,
                }"
              >
                {{ recipient }}
              </li>
            </ul>
            <p
              v-if="reportStore.settings.reportMessage"
              class="body-text-branding text-xs font-medium"
              :style="{
                ...bodyTextStyling,
                color: reportDigitalDesignContentData.digitalCover.bodyTextColour,
              }"
            >
              {{ reportStore.settings.reportMessage }}
            </p>
          </div>
          <div class="flex-none">
            <SpectreButton
              block
              size="xl"
              :style="digitalCoverButtonStyles"
              class="insights-button xl:w-1/2"
              type="button"
              @click="router.visit('?hideCover=1');"
            >
              {{ reportDigitalDesignContentData.digitalCover.buttonText }}
            </SpectreButton>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex-1"
    >
      <div
        :class="[
          'flex-none overflow-x-auto space-x-large p-base sticky top-[86px] z-20 bg-default-50',
          'xl:hidden',
        ]"
      >
        <MobileReportTabs :brand-settings="brandSettings" />
      </div>
      <div
        :class="[
          'p-base hidden divide-x items-center sticky top-0 bg-default-50 z-20',
          'xl:flex'
        ]"
      >
        <h2
          class="w-full titles-branding text-default-700 text-2xl font-bold leading-normal"
          :style="titlesStyling"
        >
          {{ ReportSectionOrder.find((title) => title.id === reportStore.currentTab)?.name }}
        </h2>
        <div class="px-large">
          <SpectreButton
            :style="digitalCoverButtonStyles"
            class="insights-button hidden xl:inline-flex text-nowrap my-sm border-l"
            @click="toggleShowModal(true)"
          >
            Contact Us
          </SpectreButton>
        </div>
      </div>
      <main
        class="pb-base text-default-300"
        @mouseenter="reportStore.setSelectedTab( null)"
      >
        <Overview
          v-if="reportStore.currentTab === 'overview' && reportStore.getSectionDisplay('overview')"
          :account-settings="accountSettings"
          :brand-settings="brandSettings"
          :custom-domain="customDomain"
          public-route
        />
        <Comparables
          v-if="reportStore.currentTab === 'comparables' && reportStore.getSectionDisplay('comparables')"
          :preferred-units="accountSettings.defaultUnits"
        />
        <LocalArea
          v-if="reportStore.currentTab === 'localArea' && reportStore.getSectionDisplay('localArea')"
          :preferred-units="accountSettings.defaultUnits"
        />
        <Gallery v-if="reportStore.currentTab === 'gallery' && reportStore.getSectionDisplay('gallery')" />
        <Market
          v-if="reportStore.currentTab === 'market' && reportStore.getSectionDisplay('market')"
          :graph-colours="accountSettings.graphColours"
        />
        <About
          v-if="reportStore.currentTab === 'about'"
          :user-settings="userSettings"
          :account-settings="accountSettings"
          :report-data="reportData"
          :report-digital-design-content-data="reportDigitalDesignContentData"
          :brand-settings="brandSettings"
        />
      </main>
    </div>
  </div>
  <SpectreModal
    :open="showModal"
    @close="toggleShowModal(false)"
  >
    <SpectreModalContent>
      <DialogTitle
        class="text-2xl font-bold text-center mb-xxs titles"
        :style="titlesStyling"
      >
        Get in touch
      </DialogTitle>
      <DialogDescription
        class="font-medium text-sm text-center mb-7 body-text-branding"
        :style="bodyTextStyling"
      >
        Fill in your details and one of our team will get in touch
      </DialogDescription>
      <form
        class="grid grid-cols-2 gap-4 mb-8"
        @submit.prevent="onSubmit"
      >
        <div class="col-span-2">
          <InsightsListbox
            :default-value="typeOptions[0]"
            :options="typeOptions"
            label="I'm interested in"
            @update:model-value="(option) => form.type = option.id"
          />
        </div>
        <div class="col-span-1">
          <SpectreField>
            <SpectreLabel>
              First name
            </SpectreLabel>
            <SpectreInput
              v-model="form.first_name"
              class="mt-2"
              :invalid="!!errors?.first_name"
              required
              autocomplete="given-name"
            />
            <SpectreErrorMessage v-if="errors?.first_name">
              {{ errors?.first_name }}
            </SpectreErrorMessage>
          </SpectreField>
        </div>
        <div class="col-span-1">
          <SpectreLabel>
            <SpectreLabel>
              Last name
            </SpectreLabel>
            <SpectreInput
              v-model="form.last_name"
              class="mt-2"
              :invalid="!!errors?.last_name"
              required
              autocomplete="family-name"
            />
            <SpectreErrorMessage v-if="errors?.last_name">
              {{ errors?.last_name }}
            </SpectreErrorMessage>
          </SpectreLabel>
        </div>
        <div class="col-span-2">
          <SpectreField>
            <SpectreLabel>
              Email
            </SpectreLabel>
            <SpectreInput
              v-model="form.email"
              class="mt-2"
              :invalid="!!errors?.email"
              required
              type="email"
              inputmode="email"
              autocomplete="email"
            />
            <SpectreErrorMessage v-if="errors?.email">
              {{ errors?.email }}
            </SpectreErrorMessage>
          </SpectreField>
        </div>
        <div class="col-span-2">
          <SpectreField>
            <SpectreLabel>
              Contact number
            </SpectreLabel>
            <SpectreInput
              v-model="form.contact_number"
              class="mt-2"
              type="tel"
              required
              :invalid="!!errors?.contact_number"
              autocomplete="tel"
              inputmode="tel"
            />
            <SpectreErrorMessage v-if="errors?.contact_number">
              {{ errors?.contact_number }}
            </SpectreErrorMessage>
          </SpectreField>
        </div>
        <div class="col-span-2">
          <SpectreField>
            <SpectreLabel>
              Message
            </SpectreLabel>
            <SpectreTextarea
              v-model="form.message"
              class="mt-2"
              :invalid="!!errors?.message"
              :rows="5"
              required
            />
            <SpectreErrorMessage v-if="errors?.message">
              {{ errors?.message }}
            </SpectreErrorMessage>
          </SpectreField>
        </div>
        <div class="col-span-2">
          <SpectreButton
            block
            type="submit"
            size="xl"
            :loading="form.processing"
            class="insights-button"
            :style="digitalCoverButtonStyles"
          >
            Send message
          </SpectreButton>
        </div>
      </form>
      <p class="text-default-400 text-center text-xs">
        Please note that this report is provided by Spectre who will send the above details to {{ reportData.account?.company?.name }} only. By submitting, you agree to the
        <a
          href="https://spectre.uk.com/terms"
          class="underline"
        >
          terms and conditions</a>
        and our privacy policy.
      </p>
    </SpectreModalContent>
  </SpectreModal>
</template>

<script setup lang="ts">
import Overview from '@/Components/Report/Overview/Index.vue';
import Comparables from '@/Components/Report/Comparables/Index.vue';
import LocalArea from '@/Components/Report/LocalArea/Index.vue';
import Gallery from '@/Components/Report/Gallery/Index.vue';
import About from '@/Components/Report/About/Index.vue';
import Market from '@/Components/Report/Market/Index.vue';
import { useReportStore } from '@/Stores/Report';
import { useComparablesStore } from '@/Stores/Comparables';
import ExpandedReportNavigation from '@/Components/ExpandedReportNavigation.vue';
import MobileReportTabs from '@/Components/ReportTabs.vue';
import { computed, onMounted } from 'vue';
import ReportSectionOrder from '@/Config/ReportSectionOrder';
import BrandLinksHead from '@/Components/BrandLinksHead.vue';
import BlankLayout from '@/Layouts/BlankLayout.vue';
import { router, useForm, usePage } from '@inertiajs/vue3';
import { useBrowserLocation, useStorage, useToggle } from '@vueuse/core';
import initialiseGtm from '@/Support/InitialiseGtm';
import collect from 'collect.js';
import { rgba } from 'polished';
import { DialogDescription, DialogTitle } from '@headlessui/vue';
import {
  SpectreErrorMessage,
  SpectreInput,
  SpectreModal,
  SpectreTextarea,
  SpectreButton,
  SpectreModalContent,
  SpectreField,
  SpectreLabel,
} from '@agentsoftware/spectre-ui';
import InsightsListbox from '@/Components/InsightsListbox.vue';
import ReportDigitalDesignContentData = App.Data.ReportDesign.Digital.ReportDigitalDesignContentData;
import ReportContentData = App.Data.Report.ReportContentData;
import BrandSettingsData = App.Data.Settings.BrandSettingsData;
import UserSettingsData = App.Data.Settings.UserSettingsData;
import AccountSettingsData = App.Data.Settings.AccountSettingsData;
import CompanyData = App.Data.CompanyData;
import CustomDomainData = App.Data.CustomDomainData;
import ReportData = App.Data.Report.ReportData;
import ReportTab = App.Enums.ReportTab;
import SortType = App.Enums.SortType;

const props = defineProps<{
  accountSettings: AccountSettingsData;
  brandSettings: BrandSettingsData;
  company: CompanyData;
  customDomain: CustomDomainData;
  errors?: Record<string, string>;
  hideCover: boolean;
  reportContentData: ReportContentData;
  reportData: ReportData;
  reportDigitalDesignContentData: ReportDigitalDesignContentData;
  streetValuationRequestUrl: string;
  userSettings: UserSettingsData | null;
}>();

defineOptions({
  layout: BlankLayout,
});

const reportStore = useReportStore();
const page = usePage();
const location = useBrowserLocation();
const [showModal, toggleShowModal] = useToggle<boolean>(false);

const typeOptions = [
  {
    id: 'buying_a_property',
    label: 'Buying a property',
  },
  {
    id: 'selling_my_property',
    label: 'Selling my property',
  },
  {
    id: 'renting_a_property',
    label: 'Renting a property',
  },
  {
    id: 'letting_my_property',
    label: 'Letting my property',
  },
  {
    id: 'something_else',
    label: 'Something else',
  },
];

const personalDetails = useStorage<{
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
}>('personal-details', {
  firstName: '',
  lastName: '',
  email: '',
  contactNumber: '',
});

const form = useForm({
  type: typeOptions[0].id,
  first_name: personalDetails.value.firstName,
  last_name: personalDetails.value.lastName,
  email: personalDetails.value.email,
  contact_number: personalDetails.value.contactNumber,
  message: '',
});

const domain = computed(() => (props.customDomain ? `https://${props.customDomain.url}` : page.props.app.url));

onMounted(() => {
  if (props.streetValuationRequestUrl) {
    reportStore.setBookViewingUrl(props.streetValuationRequestUrl);
  }

  reportStore.initialisePublicReport(JSON.parse(JSON.stringify(props.reportData)), props.hideCover);

  if (location.value.hash) {
    reportStore.setCurrentTab(location.value.hash?.substring(1) as ReportTab);
  }

  if (typeof window !== 'undefined' && props.accountSettings.googleTagManager && !collect(document.querySelectorAll('script')).pluck('src').contains(`https://www.googletagmanager.com/gtm.js?id=${props.accountSettings.googleTagManager}`)) {
    initialiseGtm(props.accountSettings.googleTagManager);
  }

  const comparablesStore = useComparablesStore();

  comparablesStore.setDefaultDistance(props.accountSettings.defaultDistance ?? 20);
  comparablesStore.setDefaultSort(props.accountSettings.defaultSort as SortType ?? 'distance_closest');
});

const address = computed(() => {
  if (reportStore.settings.anonymiseAddress) {
    return {
      houseNumber: '',
      street: reportStore.address.street,
      locality: reportStore.address.locality,
      town: reportStore.address.town,
      postcode: reportStore.address.postcode.split(' ', 1)[0],
    } as App.Data.Report.Overview.PropertyAddressData;
  }

  return reportStore.address;
});

const onSubmit = () => {
  form.post(`${domain.value}/reports/${reportStore.reportId}/contact-form/create`, {
    onSuccess: () => {
      toggleShowModal(false);
      form.reset();
    },
  });
};

const reportTitle = computed((): string => (reportStore.settings.anonymiseAddress ? `Report for ${props.reportData.content?.address.anonymisedFullAddress}` : `Report for ${reportStore.address?.houseNumber} ${reportStore.address?.street ?? ''}`));

const titlesStyling = computed(() => ({
  '--titlesFontFamily': props.brandSettings.text.heading.fontFamily,
  '--titlesFontWeight': props.brandSettings.text.heading.fontWeight,
  '--titlesColour': props.brandSettings.text.heading.colour,
}));

const bodyTextStyling = computed(() => ({
  '--bodyTextFontFamily': props.brandSettings.text.body.fontFamily,
  '--bodyTextFontWeight': props.brandSettings.text.body.fontWeight,
  '--bodyTextColour': props.brandSettings.text.body.colour,
}));

const digitalCoverButtonStyles = computed(() => {
  if (props.reportDigitalDesignContentData.digitalCover.buttonStyle === 'primary' || props.reportDigitalDesignContentData.digitalCover.buttonStyle === 'secondary') {
    return {
      '--buttonTextColour': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.digitalCover.buttonStyle as 'primary' | 'secondary'].colour}`,
      '--buttonBackgroundColour': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.digitalCover.buttonStyle as 'primary' | 'secondary'].backgroundColour}`,
      '--buttonBackgroundHoverColour': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.digitalCover.buttonStyle as 'primary' | 'secondary'].backgroundHoverColour}`,
      '--buttonTextHoverColour': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.digitalCover.buttonStyle as 'primary' | 'secondary'].hoverColour}`,
      '--buttonFontFamily': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.digitalCover.buttonStyle as 'primary' | 'secondary'].fontFamily}`,
      '--buttonFontWeight': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.digitalCover.buttonStyle as 'primary' | 'secondary'].fontWeight}`,
    };
  }

  return {
    '--buttonTextColour': `${props.reportDigitalDesignContentData.digitalCover.buttonCustomTextColour}`,
    '--buttonBackgroundColour': `${props.reportDigitalDesignContentData.digitalCover.buttonCustomBackgroundColour}`,
    '--buttonBackgroundHoverColour': `${props.reportDigitalDesignContentData.digitalCover.buttonCustomBackgroundColour}`,
    '--buttonTextHoverColour': `${props.reportDigitalDesignContentData.digitalCover.buttonCustomTextColour}`,
  };
});

const mobileButtonStyles = computed(() => {
  if (props.reportDigitalDesignContentData.mobileHeader.buttonStyle === 'primary' || props.reportDigitalDesignContentData.mobileHeader.buttonStyle === 'secondary') {
    return {
      '--buttonTextColour': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.mobileHeader.buttonStyle as 'primary' | 'secondary'].colour}`,
      '--buttonBackgroundColour': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.mobileHeader.buttonStyle as 'primary' | 'secondary'].backgroundColour}`,
      '--buttonBackgroundHoverColour': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.mobileHeader.buttonStyle as 'primary' | 'secondary'].backgroundHoverColour}`,
      '--buttonTextHoverColour': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.mobileHeader.buttonStyle as 'primary' | 'secondary'].hoverColour}`,
      '--buttonFontFamily': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.mobileHeader.buttonStyle as 'primary' | 'secondary'].fontFamily}`,
      '--buttonFontWeight': `${props.brandSettings.buttons[props.reportDigitalDesignContentData.mobileHeader.buttonStyle as 'primary' | 'secondary'].fontWeight}`,
    };
  }

  return {
    '--buttonTextColour': `${props.reportDigitalDesignContentData.mobileHeader.buttonCustomTextColour}`,
    '--buttonBackgroundColour': `${props.reportDigitalDesignContentData.mobileHeader.buttonCustomBackgroundColour}`,
    '--buttonBackgroundHoverColour': `${props.reportDigitalDesignContentData.mobileHeader.buttonCustomBackgroundColour}`,
    '--buttonTextHoverColour': `${props.reportDigitalDesignContentData.mobileHeader.buttonCustomTextColour}`,
  };
});

const mobileBackgroundColour = computed(() => {
  if (props.reportDigitalDesignContentData.mobileHeader.backgroundColourStyle === 'custom') {
    return props.reportDigitalDesignContentData.mobileHeader.backgroundCustomColour;
  }

  return props.brandSettings.colours[props.reportDigitalDesignContentData.mobileHeader.backgroundColourStyle as 'primary' | 'secondary'];
});

const headerStyles = computed(() => {
  if (props.reportDigitalDesignContentData.reportColors.style === 'brand-light') {
    return {
      '--headerBackgroundColour': '#ffffff',
      '--headerMobileBackgroundColour': mobileBackgroundColour.value,
      '--headerTextColour': props.reportDigitalDesignContentData.reportColors.titlesAndBodyTextTitleColour,
    };
  }

  if (props.reportDigitalDesignContentData.reportColors.colourStyle === 'custom') {
    return {
      '--headerBackgroundColour': props.reportDigitalDesignContentData.reportColors.customColour,
      '--headerMobileBackgroundColour': mobileBackgroundColour.value,
      '--headerTextColour': props.reportDigitalDesignContentData.reportColors.titlesAndBodyTextTitleColour,
    };
  }

  return {
    '--headerBackgroundColour': props.brandSettings.colours[props.reportDigitalDesignContentData.reportColors.colourStyle as 'primary' | 'secondary'],
    '--headerMobileBackgroundColour': mobileBackgroundColour.value,
    '--headerTextColour': props.reportDigitalDesignContentData.reportColors.titlesAndBodyTextTitleColour,
  };
});
</script>

<style scoped>
.titles-branding :deep(.titles-branding) {
  font-family: var(--titlesFontFamily);
  color: var(--titlesColour);
}

.body-text-branding :deep(.body-text-branding) {
  font-family: var(--bodyTextFontFamily);
  color: var(--bodyTextColour);
}

.insights-button {
  font-family: var(--buttonFontFamily);
  font-weight: var(--buttonFontWeight);
  color: var(--buttonTextColour);
  background-color: var(--buttonBackgroundColour);
  border-color: var(--buttonBackgroundColour);
}

.insights-button:hover {
  color: var(--buttonTextHoverColour);
  background-color: var(--buttonBackgroundHoverColour);
  border-color: var(--buttonBackgroundHoverColour);
}

.header {
  background-color: var(--headerMobileBackgroundColour);
  color: var(--headerTextColour);
  @media (min-width: 1280px) {
    background-color: var(--headerBackgroundColour);
  }
}
</style>
